import { memo, useState } from 'react'
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  getSmoothStepPath,
  type EdgeProps,
  type Edge,
} from '@xyflow/react'
import {
  Plus,
  X,
  Bot,
  UserCheck,
  GitBranch,
  Mail,
  Zap,
  FileText,
  CircleStop,
  Clock,
  Database,
  Bell,
  MessageSquare,
  CheckCircle2,
  XCircle,
} from 'lucide-react'
import { cn } from '@/lib/utils'

export interface AddButtonEdgeData {
  label?: string
  labelColor?: string
  onAddNode?: (edgeId: string, nodeType: string, subType?: string) => void
  pathType?: 'step' | 'smoothstep' | 'bezier'
  [key: string]: unknown
}

// Node options organized by category
const nodeCategories = [
  {
    label: 'AI & Review',
    items: [
      { type: 'agent', label: 'AI Agent', icon: Bot, color: '#8b5cf6' },
      { type: 'humanReview', label: 'Human Review', icon: UserCheck, color: '#06b6d4' },
    ],
  },
  {
    label: 'Logic',
    items: [
      { type: 'condition', label: 'Condition', icon: GitBranch, color: '#f59e0b' },
    ],
  },
  {
    label: 'Actions',
    items: [
      { type: 'action', subType: 'email', label: 'Send Email', icon: Mail, color: '#10b981' },
      { type: 'action', subType: 'webhook', label: 'API Call', icon: Zap, color: '#06b6d4' },
      { type: 'action', subType: 'database', label: 'Database', icon: Database, color: '#6366f1' },
      { type: 'action', subType: 'document', label: 'Document', icon: FileText, color: '#f59e0b' },
      { type: 'action', subType: 'notification', label: 'Notify', icon: Bell, color: '#ec4899' },
      { type: 'action', subType: 'sms', label: 'SMS', icon: MessageSquare, color: '#14b8a6' },
      { type: 'action', subType: 'delay', label: 'Delay', icon: Clock, color: '#8b5cf6' },
    ],
  },
  {
    label: 'Terminal',
    items: [
      { type: 'end', subType: 'success', label: 'Success', icon: CheckCircle2, color: '#10b981' },
      { type: 'end', subType: 'failure', label: 'Failure', icon: XCircle, color: '#ef4444' },
      { type: 'end', label: 'End', icon: CircleStop, color: '#64748b' },
    ],
  },
]

function AddButtonEdgeComponent({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  data,
  markerEnd,
  selected,
}: EdgeProps<Edge<AddButtonEdgeData>>) {
  const [showMenu, setShowMenu] = useState(false)

  // Use step path by default, or the specified pathType
  const pathType = data?.pathType || 'step'

  const [edgePath, labelX, labelY] = pathType === 'bezier'
    ? getBezierPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
      })
    : getSmoothStepPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
        borderRadius: pathType === 'step' ? 0 : 8,
      })

  const handleAddClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowMenu(!showMenu)
  }

  const handleNodeSelect = (nodeType: string, subType?: string) => {
    data?.onAddNode?.(id, nodeType, subType)
    setShowMenu(false)
  }

  return (
    <>
      {/* Edge path with gradient */}
      <defs>
        <linearGradient id={`edge-gradient-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={selected ? '#8b5cf6' : '#94a3b8'} />
          <stop offset="100%" stopColor={selected ? '#6366f1' : '#cbd5e1'} />
        </linearGradient>
      </defs>
      <BaseEdge
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          ...style,
          stroke: `url(#edge-gradient-${id})`,
          strokeWidth: selected ? 2.5 : 2,
        }}
      />
      <EdgeLabelRenderer>
        {/* Edge Label */}
        {data?.label && (
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -150%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'all',
            }}
            className="nodrag nopan"
          >
            <span
              className={cn(
                'px-2 py-0.5 text-xs font-medium rounded-full shadow-sm',
                data.labelColor || 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600'
              )}
            >
              {data.label}
            </span>
          </div>
        )}

        {/* Add Button */}
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            pointerEvents: 'all',
            zIndex: showMenu ? 9999 : 1,
          }}
          className="nodrag nopan"
        >
          <button
            onClick={handleAddClick}
            className={cn(
              'w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200',
              'bg-white dark:bg-gray-800 border-2 shadow-md',
              selected
                ? 'border-purple-400 dark:border-purple-500'
                : 'border-gray-200 dark:border-gray-600',
              'hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/30',
              'hover:scale-110 hover:shadow-lg',
              showMenu && 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 scale-110 shadow-lg'
            )}
          >
            {showMenu ? (
              <X className="w-3.5 h-3.5 text-purple-600" />
            ) : (
              <Plus className="w-3.5 h-3.5 text-gray-400 group-hover:text-purple-600" />
            )}
          </button>

          {/* Dropdown Menu - Categorized */}
          {showMenu && (
            <div
              className="absolute top-9 left-1/2 -translate-x-1/2 w-[200px] py-2 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700"
              style={{ zIndex: 9999 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="max-h-[320px] overflow-y-auto">
                {nodeCategories.map((category, catIdx) => (
                  <div key={catIdx}>
                    <p className="px-3 py-1.5 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                      {category.label}
                    </p>
                    {category.items.map((option, idx) => {
                      const Icon = option.icon
                      return (
                        <button
                          key={`${option.type}-${option.subType || idx}`}
                          onClick={() => handleNodeSelect(option.type, option.subType)}
                          className="flex items-center gap-2.5 w-full px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                        >
                          <div
                            className="w-7 h-7 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: `${option.color}20` }}
                          >
                            <Icon className="w-4 h-4" style={{ color: option.color }} />
                          </div>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {option.label}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </EdgeLabelRenderer>
    </>
  )
}

export const AddButtonEdge = memo(AddButtonEdgeComponent)
export default AddButtonEdge
